/**
 * ExportModal - Shows exported account contact data with copy + CSV download.
 */
import { useState } from "react";
import { X, Copy, Check, Download } from "lucide-react";
import { cn } from "../lib/utils";
import type { AccountExportItem } from "../types";
import { csvEscape } from "../../utils/csv";

interface ExportModalProps {
  items: AccountExportItem[];
  onClose: () => void;
}

function formatAsText(items: AccountExportItem[]): string {
  return items
    .filter((i) => i.contact_email)
    .map((i) => {
      const name = i.contact_name ?? i.display_name;
      return `${name} <${i.contact_email}>`;
    })
    .join("\n");
}

function formatAsCsv(items: AccountExportItem[]): string {
  const header =
    "Company,Contact Name,Contact Email,Contact Phone,City,State,Sage Customer No";
  const rows = items.map((i) =>
    [
      csvEscape(i.display_name),
      csvEscape(i.contact_name ?? ""),
      csvEscape(i.contact_email ?? ""),
      csvEscape(i.contact_phone ?? ""),
      csvEscape(i.city ?? ""),
      csvEscape(i.state ?? ""),
      csvEscape(i.sage_customer_no ?? ""),
    ].join(",")
  );
  return [header, ...rows].join("\n");
}


export function ExportModal({ items, onClose }: ExportModalProps) {
  const [copied, setCopied] = useState(false);
  const withEmail = items.filter((i) => i.contact_email);
  const withoutEmail = items.filter((i) => !i.contact_email);
  const emailText = formatAsText(items);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(emailText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadCsv = () => {
    const csv = formatAsCsv(items);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `account_contacts_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="export-modal-backdrop"
    >
      <div
        className={cn(
          "mx-4 flex max-h-[80vh] w-full max-w-lg flex-col rounded-2xl",
          "bg-surface-elevated shadow-xl"
        )}
        data-testid="export-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold text-text-primary">
            Export Results
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-text-muted hover:text-text-primary"
            data-testid="export-modal-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Stats */}
        <div className="flex gap-4 border-b border-border px-4 py-2">
          <span className="text-xs text-text-muted">
            {withEmail.length} with email
          </span>
          {withoutEmail.length > 0 && (
            <span className="text-xs text-text-muted">
              {withoutEmail.length} without email
            </span>
          )}
          <span className="text-xs text-text-muted">
            {items.length} total
          </span>
        </div>

        {/* Email list */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {withEmail.length === 0 ? (
            <p className="text-sm text-text-muted">
              No accounts with contact emails found.
            </p>
          ) : (
            <pre
              className="whitespace-pre-wrap break-all text-xs text-text-primary font-mono"
              data-testid="export-email-list"
            >
              {emailText}
            </pre>
          )}

          {withoutEmail.length > 0 && (
            <details className="mt-3">
              <summary className="cursor-pointer text-xs text-text-muted">
                {withoutEmail.length} accounts without email
              </summary>
              <ul className="mt-1 space-y-0.5">
                {withoutEmail.map((item) => (
                  <li
                    key={item.display_name}
                    className="text-xs text-text-muted"
                  >
                    {item.display_name}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 border-t border-border px-4 py-3">
          <button
            type="button"
            onClick={handleCopy}
            disabled={withEmail.length === 0}
            className={cn(
              "flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-medium",
              "bg-brand text-white transition-colors",
              withEmail.length === 0
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-brand/90"
            )}
            data-testid="export-copy-btn"
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" />
                Copy Emails
              </>
            )}
          </button>

          <button
            type="button"
            onClick={handleDownloadCsv}
            className={cn(
              "flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-medium",
              "border border-border text-text-primary transition-colors",
              "hover:bg-surface-inset"
            )}
            data-testid="export-csv-btn"
          >
            <Download className="h-3.5 w-3.5" />
            Download CSV
          </button>
        </div>
      </div>
    </div>
  );
}
